import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { ADVANCED_TIER_REGIONS } from './TierSelector';

// Pricing data
export const PRICING_DATA = {
  ua: {
    currency: 'USD',
    symbol: '$',
    warDiscount: true,
    tiers: {
      basic: { price: 11, originalPrice: 15, minUsers: 3 },
      pro: { price: 22, minUsers: 3 },
      advanced: { price: 22, minUsers: 5 },
      corporate: { price: 34, minUsers: 5 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 7, perUser: true },
      callback: { price: 39 * 0.7004 - 20, perUser: false }, // $39 × UA discount - $20 bundle
    },
  },
  eu: {
    currency: 'EUR',
    symbol: '€',
    tiers: {
      basic: { price: 20, minUsers: 3 },
      pro: { price: 34, minUsers: 3 },
      advanced: { price: 34, minUsers: 5 },
      corporate: { price: 49, minUsers: 5 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 10, perUser: true },
      callback: { price: 49 - 29, perUser: false }, // €49 base - €29 bundle
    },
  },
  asia: {
    currency: 'USD',
    symbol: '$',
    tiers: {
      basic: { price: 13.2, minUsers: 3 },
      pro: { price: 24, minUsers: 3 },
      advanced: { price: 24, minUsers: 5 },
      corporate: { price: 37, minUsers: 5 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 7, perUser: true },
      callback: { price: 39 - 29, perUser: false }, // $39 base - $29 bundle
    },
  },
  other: {
    currency: 'USD',
    symbol: '$',
    tiers: {
      basic: { price: 20, minUsers: 3 },
      pro: { price: 34, minUsers: 3 },
      advanced: { price: 34, minUsers: 5 },
      corporate: { price: 49, minUsers: 5 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 10, perUser: true },
      callback: { price: 39 - 29, perUser: false }, // $39 base - $29 bundle
    },
  },
};

// Helper: calculate yearly price (with 20% bonus) - price / 1.2, rounded up
const getYearlyPrice = (price) => Math.ceil(price / 1.2);

/**
 * Price shown for a tier column.
 *
 * Follows the global "display prices without long-term bonuses" setting so the
 * table never contradicts the monthly forecast shown next to it.
 */
export const getTierDisplayPrice = (price, showPricesWithoutBonuses) =>
  showPricesWithoutBonuses ? price : getYearlyPrice(price);

/**
 * Price shown for an add-on column.
 *
 * Most add-ons are quoted at the yearly (subscription bonus) price. Add-ons
 * flagged noYearlyDiscount are excluded from the subscription bonus in the
 * calculator, so discounting them here would show the customer a number the
 * quote never produces.
 */
export const getAddonDisplayPrice = (addonData, showPricesWithoutBonuses) => {
  const price = addonData?.price !== undefined ? addonData.price : addonData;
  if (addonData?.noYearlyDiscount) return price;
  return getTierDisplayPrice(price, showPricesWithoutBonuses);
};

// Features comparison data
// Note: names are translation keys, resolved in component with t()
export const FEATURES = [
  // Core - included or priced (AI, Chat, Callback first)
  // Pro includes the basic analytics; Corporate includes Eniq AI instead
  { key: 'aiConversation', nameKey: 'calculator.features.aiConversation', basic: 'price:aiConversation', pro: 'included:calculator.features.aiConversationBasicLevel', advanced: 'price:aiConversation', corporate: 'included:calculator.features.aiConversationProLevel' },
  { key: 'omnichannelChat', nameKey: 'calculator.features.omnichannelChat', basic: 'price:omnichannelChat', pro: 'included', advanced: 'price:omnichannelChat', corporate: 'included' },
  { key: 'callback', nameKey: 'calculator.features.callback', basic: 'price:callback', pro: 'included', advanced: 'included', corporate: 'included' },
  // Pro features
  { key: 'realTimeReports', nameKey: 'calculator.features.realTimeReports', basic: false, pro: true, advanced: true, corporate: true },
  { key: 'agentStatuses', nameKey: 'calculator.features.agentStatuses', basic: false, pro: true, advanced: true, corporate: true },
  { key: 'callWhisper', nameKey: 'calculator.features.callWhisper', basic: false, pro: true, advanced: true, corporate: true },
  { key: 'csat', nameKey: 'calculator.features.csat', basic: false, pro: true, advanced: true, corporate: true },
  { key: 'customerSuccessTeam', nameKey: 'calculator.features.customerSuccessTeam', basic: false, pro: true, advanced: true, corporate: true },
  // Advanced + Corporate
  { key: 'powerDialer', nameKey: 'calculator.features.powerDialer', basic: false, pro: false, advanced: true, corporate: true },
  { key: 'autoMissedProcessing', nameKey: 'calculator.features.autoMissedProcessing', basic: false, pro: false, advanced: true, corporate: true },
  { key: 'prioritySupport', nameKey: 'calculator.features.prioritySupport', basic: false, pro: false, advanced: true, corporate: true },
  { key: 'customLookerReports', nameKey: 'calculator.features.customLookerReports', basic: false, pro: false, advanced: true, corporate: true },
  { key: 'customUserRoles', nameKey: 'calculator.features.customUserRoles', basic: false, pro: false, advanced: true, corporate: true, comingSoon: true },
  // Corporate only
  { key: 'dedicatedVipCs', nameKey: 'calculator.features.dedicatedVipCs', basic: false, pro: false, advanced: false, corporate: true },
  { key: 'customCrmIntegration', nameKey: 'calculator.features.customCrmIntegration', basic: false, pro: false, advanced: false, corporate: true },
  // Standard vs Pro differences live in EniqCompareDialog — that comparison
  // describes the add-on, not the package tier.
];

// Feature cell renderer
const FeatureCell = ({ value, symbol, addons, t, showPricesWithoutBonuses }) => {
  // Check if it's a price reference (Basic tier - grey text)
  if (typeof value === 'string' && value.startsWith('price:')) {
    const addonKey = value.replace('price:', '');
    const addonData = addons[addonKey];
    const perUser = addonData?.perUser;
    const prefix = addonData?.prefix || '';
    const displayPrice = getAddonDisplayPrice(addonData, showPricesWithoutBonuses);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          sx={{
            color: '#666',
            fontWeight: 500,
            fontSize: '0.8rem',
          }}
        >
          {prefix}+{symbol}{displayPrice}
        </Typography>
        {perUser && (
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.6rem' }}>
            {t('calculator.perUser', '/user')}
          </Typography>
        )}
      </Box>
    );
  }

  // INCLUDED badge for Pro/Corporate (light green bg, dark green text).
  // 'included:<i18nKey>' names what exactly is included instead of a bare badge.
  if (value === 'included' || (typeof value === 'string' && value.startsWith('included:'))) {
    const label = value.startsWith('included:')
      ? t(value.replace('included:', ''))
      : t('calculator.included', 'INCLUDED');

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: 'rgba(0, 200, 83, 0.1)',
          color: '#00C853',
          fontWeight: 600,
          fontSize: '0.65rem',
          height: 20,
        }}
      />
    );
  }

  // Included (true)
  if (value === true) {
    return (
      <CheckCircleIcon
        sx={{
          color: '#00C853',
          fontSize: 22,
        }}
      />
    );
  }

  // Not available (false)
  return (
    <CancelIcon
      sx={{
        color: '#ddd',
        fontSize: 22,
      }}
    />
  );
};

/**
 * TierCompareDialog - Modal with feature comparison table
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Close handler
 * @param {string} region - Region for pricing (ua, eu, asia, other)
 * @param {string} selectedTier - Currently selected tier (basic, pro, advanced, corporate)
 * @param {function} [onTierSelect] - Optional tier selection handler. If not provided, dialog is read-only.
 * @param {boolean} [showAdvancedTier] - Whether to show the Advanced tier column
 */
const TierCompareDialog = ({ open, onClose, region, selectedTier, onTierSelect, showAdvancedTier, showPricesWithoutBonuses = false }) => {
  const { t } = useTranslation();
  const pricing = PRICING_DATA[region] || PRICING_DATA.other;
  const { symbol, addons, tiers, warDiscount } = pricing;

  // Read-only mode when onTierSelect is not provided
  const isReadOnly = !onTierSelect;

  // Build tier columns: include Advanced when enabled for this region
  const tierColumns = useMemo(() => {
    const columns = [
      { key: 'basic', name: 'Basic', price: tiers.basic.price, originalPrice: tiers.basic.originalPrice },
      { key: 'pro', name: 'Pro', price: tiers.pro.price, popular: true },
    ];

    if (showAdvancedTier && ADVANCED_TIER_REGIONS.includes(region)) {
      columns.push({ key: 'advanced', name: 'Advanced', price: tiers.advanced.price });
    }

    columns.push({ key: 'corporate', name: 'Corporate', price: tiers.corporate.price });

    return columns;
  }, [tiers, showAdvancedTier, region]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('calculator.comparePlansTitle', 'Порівняння тарифів')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(85vh - 100px)', overflowX: 'hidden' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: '#fafbfc',
                    width: '35%',
                    borderBottom: '2px solid rgba(0,0,0,0.08)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  {t('calculator.feature', 'Функція')}
                </TableCell>
                {tierColumns.map((tier) => {
                  const isSelected = selectedTier === tier.key;
                  const showDiscount = region === 'ua' && tier.key === 'basic' && tier.originalPrice;

                  return (
                    <TableCell
                      key={tier.key}
                      align="center"
                      onClick={isReadOnly ? undefined : () => onTierSelect(tier.key)}
                      sx={{
                        fontWeight: 600,
                        bgcolor: isSelected ? '#e8f5e9' : tier.popular ? '#f1f8e9' : '#fafbfc',
                        borderBottom: isSelected ? '2px solid #00C853' : '2px solid rgba(0,0,0,0.08)',
                        cursor: isReadOnly ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        '&:hover': isReadOnly ? {} : {
                          bgcolor: '#e0f2e9',
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? '#00C853' : tier.popular ? '#00C853' : '#222',
                          }}
                        >
                          {tier.name}
                          {tier.popular && (
                            <Chip
                              label="★"
                              size="small"
                              sx={{
                                ml: 0.5,
                                height: 16,
                                fontSize: '0.6rem',
                                bgcolor: '#FF9800',
                                color: '#fff',
                              }}
                            />
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {showDiscount && (
                            <Typography
                              variant="caption"
                              sx={{ textDecoration: 'line-through', color: '#999' }}
                            >
                              {symbol}{getTierDisplayPrice(tier.originalPrice, showPricesWithoutBonuses)}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: isSelected ? '#00C853' : '#666' }}
                          >
                            {symbol}{getTierDisplayPrice(tier.price, showPricesWithoutBonuses)}{t('calculator.perUser', '/user')}
                          </Typography>
                        </Box>
                        {/* War discount text for UA Basic */}
                        {showDiscount && warDiscount && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#0057B7',
                              fontSize: '0.6rem',
                              display: 'block',
                              mt: 0.25,
                            }}
                          >
                            🇺🇦 {t('calculator.uaBusinessDiscount', 'Знижка для українського бізнесу')}
                          </Typography>
                        )}
                        {isSelected && (
                          <Typography variant="caption" sx={{ color: '#00C853', fontWeight: 500 }}>
                            ✓ {t('calculator.selected', 'Вибрано')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {FEATURES.map((feature, index) => (
                <TableRow
                  key={feature.key}
                  sx={{
                    bgcolor: feature.comingSoon
                      ? 'rgba(244, 67, 54, 0.04)'
                      : index % 2 === 0 ? '#fff' : '#fafbfc',
                    '&:hover': {
                      bgcolor: feature.comingSoon
                        ? 'rgba(244, 67, 54, 0.08)'
                        : 'rgba(0, 200, 83, 0.04)',
                    },
                  }}
                >
                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {t(feature.nameKey, feature.key)}
                      </Typography>
                      {feature.comingSoon && (
                        <Chip
                          label={t('calculator.comingSoon', 'Coming Soon')}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 18,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  {tierColumns.map((tier) => (
                    <TableCell
                      key={tier.key}
                      align="center"
                      sx={{
                        py: 1.25,
                        bgcolor: selectedTier === tier.key ? 'rgba(0, 200, 83, 0.04)' : 'transparent',
                      }}
                    >
                      <FeatureCell
                        value={feature[tier.key]}
                        symbol={symbol}
                        addons={addons}
                        t={t}
                        showPricesWithoutBonuses={showPricesWithoutBonuses}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer hint - only show in interactive mode */}
        {!isReadOnly && (
          <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)', bgcolor: '#fafbfc' }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              💡 {t('calculator.compareTip', 'Натисніть на колонку тарифу щоб вибрати його')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TierCompareDialog;
